// iter570 — Extracted from BankStatementsPage.jsx (Notes tab).
// Pure presentation; parent owns the notes list and delete handler.
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StickyNote, Bookmark, Trash2 } from 'lucide-react';

export function NotesTab({ notes, onDelete }) {
  return (
    <div className="space-y-3" data-testid="notes-content-inner">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <StickyNote size={14} className="text-emerald-600" />
            Month-End Notes
          </p>
          <p className="text-xs text-muted-foreground">Persisted reconciliation narratives — visible to your accountant and auditor as part of the statement's record.</p>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Tip: open the Reconciliation tab → click <b>AI Explain All</b> → click <b>Save as Month-End Note</b>.
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="border-2 border-dashed border-muted-foreground/20 rounded-xl p-8 text-center text-sm text-muted-foreground">
          <StickyNote size={28} className="mx-auto mb-2 opacity-40" />
          No notes saved yet.<br />
          <span className="text-[11px]">Use "Save as Month-End Note" on the AI report to capture your monthly-close commentary here.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <Card key={note.id} className="border-emerald-200 bg-emerald-50/30" data-testid={`month-end-note-${note.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-2 min-w-0">
                    <Bookmark size={14} className="text-emerald-700 shrink-0" />
                    <span className="truncate" title={note.title}>{note.title}</span>
                    {note.source === 'ai_batch_explain' && (
                      <Badge variant="outline" className="text-[9px] font-normal border-purple-300 text-purple-700">AI</Badge>
                    )}
                  </span>
                  <div className="flex items-center gap-2 text-[10px] font-normal text-muted-foreground">
                    <span>{(note.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {note.created_by_name && <span>· {note.created_by_name}</span>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(note.id)}
                      className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50"
                      data-testid={`month-end-note-delete-${note.id}`}
                      title="Delete this note"
                    >
                      <Trash2 size={11} />
                    </Button>
                  </div>
                </CardTitle>
                {note.discrepancy_count > 0 && (
                  <div className="text-[10px] text-muted-foreground flex items-center gap-3">
                    <span>{note.discrepancy_count} discrepancies analysed</span>
                    {note.total_gap > 0 && <span className="text-error">Combined gap: SR {Number(note.total_gap).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="whitespace-pre-wrap text-xs leading-relaxed p-3 bg-white rounded-lg border border-emerald-100" data-testid={`month-end-note-body-${note.id}`}>
                  {note.narrative}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
